import { OwcGrid, OwcTypography } from "@one/react";
import CustomChip from "../CustomChip";
import { useSelector } from "react-redux";
import { selectIsPredOrGredUser } from "../../../selectors/user";
import { useEffect, useMemo, useState } from "react";
import ActionType from "./BookingEventDetails/ActionType";
import ApprovalStatus from "./BookingEventDetails/ApprovalStatus";
import AssistanceDetails from "./BookingEventDetails/AssistanceDetails";
import BookedForUsers from "./BookingEventDetails/BookedForUsers";
import CreatedByCreatedOn from "./BookingEventDetails/CreatedByCreatedOn";
import DateTimeRange from "./BookingEventDetails/DateTimeRange";
import ElnDetails from "./BookingEventDetails/ElnDetails";
import InlineEditableField from "../InlineEditableField/InlineEditableField";
import { InlineRichDescriptionField } from "../RichDescription";
import InformationFromInlineEdit from "./BookingEventDetails/InformationFromInlineEdit";
import InformationProjectInlineEdit from "./BookingEventDetails/InformationProjectInlineEdit";
import { APPROVE_BOOKING, UPDATE_INFORMATION_BOOKING, QUICK_EDIT_BOOKING } from "../../../gql/bookingapi/mutations";
import { INFORMATION_BOOKING_IDS_BY_BATCH_ID } from "../../../gql/bookingapi/queries";
import { snackbarService } from "@one/web-components/dist/index.cjs.js";
import { quickEditTracker } from "../../../features/booking-operations/quickEditTracker";
import { formatUserDisplay, uniqList, USER_FORMAT_PRESETS } from "../../../utils/helpers/text";
import ProjectInfo from "./BookingEventDetails/ProjectInfo";
import ChangeBookingEndTimeModal from "../../../features/my-booking/ChangeBookingEndTime/ChangeBookingEndTimeModal";
import useChangeEndTimeModal from "../../../features/my-booking/ChangeBookingEndTime/hooks/useChangeEndTimeModal";
import isUserCreatorParticipantOrAdmin from "../../../utils/helpers/user/isUserCreatorParticipantOrAdmin";

export const calculateBookingDurationHours = (booking) => {
  const startRaw = booking?.dateFrom || booking?.start;
  const endRaw = booking?.dateTo || booking?.end;
  if (!startRaw || !endRaw) return null;

  const start = new Date(startRaw);
  const end = new Date(endRaw);
  const diffMs = end.getTime() - start.getTime();
  if (!Number.isFinite(diffMs) || diffMs <= 0) return null;

  return Math.max(0.5, Math.round((diffMs / 3600000) * 2) / 2);
};

const isValidHalfHourDuration = (value) =>
  Number.isFinite(value) && value >= 0.5 && Math.abs(value * 2 - Math.round(value * 2)) < 1e-9;

const isDurationInputEmpty = (rawDuration) =>
  rawDuration === undefined || rawDuration === null || String(rawDuration).trim() === "";

const resolveDurationInput = ({ rawDuration, bookingDurationHours }) => {
  if (isDurationInputEmpty(rawDuration)) {
    if (Number(bookingDurationHours) > 0) {
      return { duration: bookingDurationHours, errorMessage: "" };
    }
    return {
      duration: null,
      errorMessage: "Duration is required because booking duration cannot be calculated",
    };
  }

  const parsed = Number(rawDuration);
  if (!isValidHalfHourDuration(parsed)) {
    return { duration: null, errorMessage: "Duration must be at least 0.5h and use 0.5h steps" };
  }

  return { duration: parsed, errorMessage: "" };
};

const showSnackbarMessage = ({ type, message, duration = 5000 }) => {
  try {
    snackbarService.show({
      type,
      message,
      duration,
    });
  } catch (_) {}
};

const getInlineStateFromEventData = (eventData) => ({
  approvalStatus: eventData?.approval?.status || "",
  assistantId: eventData?.assistent?.[0] || "",
  assistantTime: eventData?.assistentTime ?? null,
});

const getBookingId = (eventData) => {
  if (eventData?.bookingId) return eventData.bookingId;
  if (eventData?.id) return eventData.id;
  return eventData?.entryId;
};

const isBookingAdminUser = (user) => {
  if (!Array.isArray(user?.roles)) return false;
  return user.roles.some((role) => role === "BookingAdmin");
};

const canEditBookingInline = (eventData, isBookingAdmin) => {
  if (!eventData?.id) return false;
  return isBookingAdmin;
};

const canEditApprovalForEquipmentCapabilities = (eventData) => {
  const supportsAssistance = !!eventData?.assistanceDeclaration?.requiresAssistance;
  const supportsHazardous = !!eventData?.hazardousDeclaration?.hasHazardousMaterials;
  return supportsAssistance || supportsHazardous;
};

const getBookingEntriesCount = (bookingEntries) => {
  if (!Array.isArray(bookingEntries)) return 0;
  return bookingEntries.length;
};

const buildEffectiveEventData = (eventData, inlineState) => {
  const effectiveApprovalStatus = inlineState.approvalStatus || eventData?.approval?.status;
  const effectiveAssistantIds = inlineState.assistantId ? [inlineState.assistantId] : [];
  const effectiveAssistantTime = inlineState.assistantTime;

  return {
    ...eventData,
    assistent: effectiveAssistantIds,
    assistentTime: effectiveAssistantTime,
    approval: effectiveApprovalStatus
      ? {
          ...(eventData?.approval || {}),
          status: effectiveApprovalStatus,
        }
      : eventData?.approval,
  };
};

const resolveAssistantName = (effectiveEventData, usersList) => {
  if (!effectiveEventData?.assistent || !effectiveEventData?.assistent[0]) return "Not yet assigned";

  const assistantId = effectiveEventData.assistent[0];
  const assistantUser = usersList?.find((userItem) => userItem.userId === assistantId);
  if (assistantUser) {
    return formatUserDisplay(assistantUser, USER_FORMAT_PRESETS.nameOnly);
  }

  return assistantId;
};

const stopQuickEditLoading = (stopLoading) => {
  if (stopLoading) stopLoading();
};

const handleQuickEditFailure = ({ stopLoading, setValue, previousValue, errorMessage }) => {
  stopQuickEditLoading(stopLoading);
  setValue(previousValue);
  showSnackbarMessage({
    type: "error",
    message: errorMessage,
  });
};

const finishQuickEditWithoutTracking = ({ stopLoading, onUpdateComplete }) => {
  stopQuickEditLoading(stopLoading);
  if (onUpdateComplete) onUpdateComplete();
};

const registerQuickEditTracking = ({ payload, stopLoading, onUpdateComplete, setValue, previousValue }) => {
  quickEditTracker
    .register(payload.commandId, "UPDATE", {
      onSuccess: () => {
        stopQuickEditLoading(stopLoading);
        if (onUpdateComplete) onUpdateComplete();
      },
      onError: () => {
        stopQuickEditLoading(stopLoading);
        setValue(previousValue);
      },
    })
    .catch(() => {});
};

const checkApprovalTransition = ({
  nextStatus,
  effectiveApprovalStatus,
  isAssistantRequiredForApproval,
  hasAssistant,
  rejectionReason,
}) => {
  if (nextStatus === "APPROVED") {
    if (effectiveApprovalStatus !== "PENDING") return false;
    if (!isAssistantRequiredForApproval) return true;
    return hasAssistant;
  }
  if (nextStatus === "REJECTED") return Boolean(rejectionReason);
  if (nextStatus === "PENDING") return effectiveApprovalStatus === "APPROVED";
  return false;
};

const resolveApprovalSaveParams = ({ options, inlineState, eventData }) => {
  const selectedAssistantId = options?.assistantId || inlineState.assistantId;
  const rejectionReason = (options?.rejectionReason || "").trim();
  const assistantStr = selectedAssistantId != null ? String(selectedAssistantId) : "";
  const hasAssistant = Boolean(assistantStr.trim());
  const rawTime = Number(inlineState.assistantTime);
  const bookingDurationHours = calculateBookingDurationHours(eventData);

  let approvalAssistantTime = rawTime > 0 ? rawTime : null;
  let validationError = "";

  if (hasAssistant) {
    const durationResolution = resolveDurationInput({
      rawDuration: options?.assistantDuration,
      bookingDurationHours,
    });
    approvalAssistantTime = durationResolution.duration;
    validationError = durationResolution.errorMessage;
  }

  return { selectedAssistantId, rejectionReason, hasAssistant, approvalAssistantTime, validationError };
};

// The person the booking is FOR shouldn't be assignable as their own assistant, but the
// creator (e.g. an admin booking on someone else's behalf) must remain selectable.
const filterOutReservedForUsers = (usersList, booking) => {
  const reservedForIds = [...(booking?.reservedForUsers || []), ...(booking?.reservedForEmails || [])];
  if (!reservedForIds.length || !Array.isArray(usersList)) return usersList || [];
  return usersList.filter(
    (u) => !reservedForIds.includes(u.userId) && !reservedForIds.includes(u.email) && !reservedForIds.includes(u.name)
  );
};

const buildApprovalStatusProps = ({
  effectiveEventData,
  canQuickEditApprovalAndAssistant,
  activeEditor,
  handleApprovalStatusSave,
  handleStatusEditModeChange,
  usersList,
  inlineState,
  isAssistantRequiredForApproval,
}) => ({
  approvalStatus: effectiveEventData?.approval?.status,
  allowEdit: canQuickEditApprovalAndAssistant && activeEditor !== "assistance",
  onSaveStatus: handleApprovalStatusSave,
  onEditModeChange: handleStatusEditModeChange,
  users: filterOutReservedForUsers(usersList, effectiveEventData),
  assistantId: inlineState.assistantId || "",
  requiresAssistant: isAssistantRequiredForApproval,
});

const buildAssistanceDetailsProps = ({
  effectiveEventData,
  assistantName,
  client,
  onUpdateComplete,
  onAssistanceUpdateStarted,
  onAssistanceUpdateComplete,
  assistanceUpdateCallbackRef,
  allowAssistanceEdit,
  activeEditor,
  canQuickEditApprovalAndAssistant,
  startLoading,
  stopLoading,
  handleAssistanceSaved,
  handleAssistanceEditModeChange,
}) => ({
  assistanceDeclaration: effectiveEventData?.assistanceDeclaration,
  assistanceDuration: effectiveEventData?.assistentTime,
  assistantName,
  approvalStatus: effectiveEventData?.approval?.status,
  client,
  booking: effectiveEventData,
  onUpdateComplete,
  onAssistanceUpdateStarted,
  onAssistanceUpdateComplete,
  assistanceUpdateCallbackRef,
  allowEdit: allowAssistanceEdit && canQuickEditApprovalAndAssistant && activeEditor !== "status",
  startLoading,
  stopLoading,
  onAssistanceSaved: handleAssistanceSaved,
  onEditModeChange: handleAssistanceEditModeChange,
});

const ApprovalStatusSection = ({ isRunEvent, approvalStatusProps }) => {
  if (!isRunEvent) return null;

  return (
    <OwcGrid
      item
      xl={1}
      l={1}
      m={1}
      xs={1}
      s={1}
      style={{
        alignSelf: "start",
        paddingBottom: "0.25rem",
      }}
    >
      <ApprovalStatus {...approvalStatusProps} />
    </OwcGrid>
  );
};

const HazardousMaterialsSection = ({
  isRunEvent,
  canEditInline,
  eventData,
  hazardousValue,
  handleInlineEditStart,
  handleHazardousSave,
}) => {
  if (!isRunEvent) return null;

  return (
    <OwcGrid
      item
      xl={1}
      l={1}
      m={1}
      xs={1}
      s={2}
      style={{
        alignSelf: "start",
        paddingBottom: "0.25rem",
      }}
    >
      <InlineEditableField
        label="Hazardous materials details"
        value={hazardousValue}
        allowEdit={canEditInline && !!eventData?.hazardousDeclaration?.hasHazardousMaterials}
        placeholder="Enter hazardous materials list"
        onEditStart={handleInlineEditStart}
        onSave={handleHazardousSave}
        displayFormatter={(val) => (val && String(val).trim() ? String(val) : "-")}
      />
    </OwcGrid>
  );
};

const AssistanceDetailsSection = ({ isRunEvent, assistanceDetailsProps }) => {
  if (!isRunEvent) return null;

  return (
    <OwcGrid
      item
      xl={1}
      l={1}
      m={1}
      xs={1}
      s={2}
      style={{
        alignSelf: "start",
        paddingBottom: "0.25rem",
      }}
    >
      <AssistanceDetails {...assistanceDetailsProps} />
    </OwcGrid>
  );
};

const ElnDetailsSection = ({ isRunEvent, elnId }) => {
  if (!isRunEvent) return null;

  return (
    <OwcGrid
      item
      xl={1}
      l={1}
      m={1}
      xs={1}
      s={2}
      style={{
        alignSelf: "start",
        paddingBottom: "0.25rem",
      }}
    >
      <ElnDetails elnId={elnId} />
    </OwcGrid>
  );
};

const BookingEventDetails = ({
  eventData,
  title = "Event Details",
  client,
  onUpdateComplete,
  onAssistanceUpdateStarted,
  onAssistanceUpdateComplete,
  assistanceUpdateCallbackRef,
  allowAssistanceEdit = true,
  startLoading,
  stopLoading,
}) => {
  const user = useSelector((store) => store.user);
  const usersList = useSelector((state) => state.users.users);
  const [activeEditor, setActiveEditor] = useState(null);
  const syncedApprovalStatus = eventData?.approval?.status || "";
  const syncedAssistantId = eventData?.assistent?.[0] || "";
  const syncedAssistantTime = eventData?.assistentTime ?? null;
  const [inlineState, setInlineState] = useState(getInlineStateFromEventData(eventData));

  useEffect(() => {
    setInlineState({
      approvalStatus: syncedApprovalStatus,
      assistantId: syncedAssistantId,
      assistantTime: syncedAssistantTime,
    });
  }, [eventData?.id, eventData?.bookingId, syncedApprovalStatus, syncedAssistantId, syncedAssistantTime]);

  const effectiveApprovalStatus = inlineState.approvalStatus || eventData?.approval?.status;
  const isAssistantRequiredForApproval = !!eventData?.assistanceDeclaration?.requiresAssistance;
  const effectiveEventData = buildEffectiveEventData(eventData, inlineState);

  const bookingId = getBookingId(eventData);
  const isRunEvent = eventData?.type === "Run";
  const isBookingAdmin = isBookingAdminUser(user);
  const canEditInline = canEditBookingInline(eventData, isBookingAdmin);
  const canEditAssistanceForCapabilities = isRunEvent;
  const canEditApprovalForCapabilities = canEditApprovalForEquipmentCapabilities(eventData);
  // Assistant/approval must remain editable for in-progress and past bookings (business requirement).
  const canQuickEditBase = canEditInline;
  const canQuickEditApproval = canQuickEditBase && canEditApprovalForCapabilities;
  const canQuickEditAssistance = canQuickEditBase && canEditAssistanceForCapabilities;

  const {
    isChangeEndTimeOpen,
    bookingForEndTimeChange,
    handleOpenChangeEndTimeModal,
    handleCloseChangeEndTimeModal,
    handleSaveEndTime,
  } = useChangeEndTimeModal({
    initialSelectedResourceId: eventData?.resource,
    onSave: onUpdateComplete,
    client,
  });
  const canChangeEndTime = isUserCreatorParticipantOrAdmin(user, eventData);

  const buildMutationActors = () => ({
    approvedBy: user?.user,
    approvedByEmail: user?.email,
    createdBy: user?.user,
    createdByEmail: user?.email,
  });

  const executeApprovalMutation = async (approvalPayload) => {
    try {
      if (startLoading) startLoading();
      const actors = buildMutationActors();
      const result = await client.mutate({
        mutation: APPROVE_BOOKING,
        variables: {
          bookingId,
          approval: {
            ...approvalPayload,
            approvedBy: actors.approvedBy,
            approvedByEmail: actors.approvedByEmail,
          },
          createdBy: actors.createdBy,
          createdByEmail: actors.createdByEmail,
        },
      });

      const payload = result?.data?.approveBooking;
      if (!payload?.success) return false;

      // Defer the timeline refresh until the async BookingCommand has actually been
      // processed, otherwise the refresh can race ahead of the backend update and
      // show stale data (e.g. a removed assistant name reappearing).
      if (payload.commandId) {
        quickEditTracker
          .register(payload.commandId, "UPDATE", {
            onSuccess: () => {
              stopQuickEditLoading(stopLoading);
              if (onUpdateComplete) onUpdateComplete();
            },
            onError: () => {
              stopQuickEditLoading(stopLoading);
            },
          })
          .catch(() => {});
      } else {
        finishQuickEditWithoutTracking({ stopLoading, onUpdateComplete });
      }

      return true;
    } catch (error) {
      if (stopLoading) stopLoading();
      return false;
    }
  };

  const applyApprovedInlineState = (selectedAssistantId, approvalAssistantTime) => {
    setInlineState((prev) => ({
      ...prev,
      approvalStatus: "APPROVED",
      assistantId: selectedAssistantId || prev.assistantId,
      assistantTime: prev.assistantTime || approvalAssistantTime,
    }));
  };

  const applyRejectedInlineState = () => {
    setInlineState((prev) => ({
      ...prev,
      approvalStatus: "REJECTED",
      assistantId: "",
      assistantTime: null,
    }));
  };

  const applyPendingInlineState = () => {
    setInlineState((prev) => ({
      ...prev,
      approvalStatus: "PENDING",
      assistantId: "",
      assistantTime: null,
    }));
  };

  const saveApprovedStatus = async (selectedAssistantId, approvalAssistantTime) => {
    const approvalPayload = {
      status: "APPROVED",
      assistant: [selectedAssistantId],
      assistantTime: approvalAssistantTime,
    };

    const success = await executeApprovalMutation(approvalPayload);
    if (success) {
      applyApprovedInlineState(selectedAssistantId, approvalAssistantTime);
    }
    return success;
  };

  const saveRejectedStatus = async (rejectionReason) => {
    const success = await executeApprovalMutation({
      status: "REJECTED",
      rejectionReason,
    });
    if (success) {
      applyRejectedInlineState();
    }
    return success;
  };

  const savePendingStatus = async () => {
    const success = await executeApprovalMutation({
      status: "PENDING",
      assistant: [],
      assistantTime: null,
    });
    if (success) {
      applyPendingInlineState();
    }
    return success;
  };

  const handleApprovalStatusSave = async (nextStatus, options = {}) => {
    const normalizedNext = (nextStatus || "").toUpperCase();
    if (!canQuickEditApproval) return false;
    if (!normalizedNext || !bookingId || !effectiveApprovalStatus) return false;
    if (normalizedNext === effectiveApprovalStatus) return true;

    const params = resolveApprovalSaveParams({ options, inlineState, eventData });
    if (params.validationError) {
      showSnackbarMessage({ type: "error", message: params.validationError });
      return false;
    }

    const isAllowed = checkApprovalTransition({
      nextStatus: normalizedNext,
      effectiveApprovalStatus,
      isAssistantRequiredForApproval,
      ...params,
    });
    if (!isAllowed) return false;

    const statusHandlers = {
      APPROVED: () => saveApprovedStatus(params.selectedAssistantId, params.approvalAssistantTime),
      REJECTED: () => saveRejectedStatus(params.rejectionReason),
      PENDING: () => savePendingStatus(),
    };

    const handler = statusHandlers[normalizedNext];
    if (!handler) return false;
    return handler();
  };

  const handleAssistanceSaved = ({ assistantId, assistantDuration, approvalStatus }) => {
    setInlineState((prev) => ({
      ...prev,
      assistantId: assistantId || "",
      assistantTime: assistantId ? assistantDuration : null,
      approvalStatus: approvalStatus || prev.approvalStatus,
    }));
  };

  const handleStatusEditModeChange = (isEditing) => {
    setActiveEditor((prev) => {
      if (isEditing) return "status";
      return prev === "status" ? null : prev;
    });
  };

  const handleAssistanceEditModeChange = (isEditing) => {
    setActiveEditor((prev) => {
      if (isEditing) return "assistance";
      return prev === "assistance" ? null : prev;
    });
  };

  const assistantName = resolveAssistantName(effectiveEventData, usersList);

  const [descriptionValue, setDescriptionValue] = useState(eventData?.description ?? "");
  const [richDescriptionValue, setRichDescriptionValue] = useState(
    eventData?.richDescription ?? eventData?.description ?? "",
  );
  const [hazardousValue, setHazardousValue] = useState(eventData?.hazardousDeclaration?.hazardousMaterialsList ?? "");

  // Re-sync local edit state only when a DIFFERENT booking is opened. Syncing
  // on every change of `eventData.richDescription` would clobber the freshly
  // saved value with the stale Apollo cache value during the brief window
  // between the QuickEdit success and the refetch propagating - the popup
  // would flash the old description back. Booking id changes only when the
  // user opens another booking, which is the only moment a hard reset is
  // wanted.
  useEffect(() => {
    setDescriptionValue(eventData?.description ?? "");
    setRichDescriptionValue(eventData?.richDescription ?? eventData?.description ?? "");
    setHazardousValue(eventData?.hazardousDeclaration?.hazardousMaterialsList ?? "");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [eventData?.id, eventData?.bookingId, eventData?.informationBookingId]);

  const [hasShownMultiSlotWarning, setHasShownMultiSlotWarning] = useState(false);
  const handleInlineEditStart = () => {
    const slotCount = getBookingEntriesCount(eventData?.bookingEntries);
    if (slotCount > 1 && !hasShownMultiSlotWarning) {
      try {
        snackbarService.show({
          type: "warning",
          message: `This booking has ${slotCount} slots. Editing will affect all slots in the booking.`,
          duration: 5000,
        });
      } catch (_) {}
      setHasShownMultiSlotWarning(true);
    }
  };

  const saveInlineTextField = async ({ nextValue, currentValue, setValue, buildInput, errorMessage }) => {
    if (!bookingId) return;

    const trimmed = nextValue.trim();
    const normalizedCurrent = currentValue || "";
    if (trimmed === normalizedCurrent.trim()) return;

    const previousValue = currentValue;
    if (startLoading) startLoading();

    try {
      const result = await client.mutate({
        mutation: QUICK_EDIT_BOOKING,
        variables: { input: buildInput(trimmed, bookingId) },
      });
      const payload = result?.data?.quickEditBooking;
      if (!payload?.success) {
        handleQuickEditFailure({
          stopLoading,
          setValue,
          previousValue,
          errorMessage: payload?.message || errorMessage,
        });
        return;
      }

      setValue(trimmed);

      if (payload?.commandId) {
        registerQuickEditTracking({
          payload,
          stopLoading,
          onUpdateComplete,
          setValue,
          previousValue,
        });
      } else {
        finishQuickEditWithoutTracking({
          stopLoading,
          onUpdateComplete,
        });
      }
    } catch (error) {
      handleQuickEditFailure({
        stopLoading,
        setValue,
        previousValue,
        errorMessage: error?.message || errorMessage,
      });
    }
  };

  const slotsCount = getBookingEntriesCount(eventData?.bookingEntries);
  const approvalStatusProps = buildApprovalStatusProps({
    effectiveEventData,
    canQuickEditApprovalAndAssistant: canQuickEditApproval,
    activeEditor,
    handleApprovalStatusSave,
    handleStatusEditModeChange,
    usersList,
    inlineState,
    isAssistantRequiredForApproval,
  });
  const assistanceDetailsProps = buildAssistanceDetailsProps({
    effectiveEventData,
    assistantName,
    client,
    onUpdateComplete,
    onAssistanceUpdateStarted,
    onAssistanceUpdateComplete,
    assistanceUpdateCallbackRef,
    allowAssistanceEdit,
    activeEditor,
    canQuickEditApprovalAndAssistant: canQuickEditAssistance,
    startLoading,
    stopLoading,
    handleAssistanceSaved,
    handleAssistanceEditModeChange,
  });

  const isInformationBooking = !!eventData?.isInformationBooking;
  const informationSlotId = eventData?.informationBookingId || eventData?.bookingId || eventData?.id;
  const informationBatchId = eventData?.batchId || null;
  const storeUserForProjects = useSelector((state) => state.user);

  // Build the whitelisted patch for an Information slot edit (ISO 7367645).
  // Editable scope: description / project / reservedForUsers. Other fields
  // on the BE row (dateFrom/dateTo/inventoryId/color/fcolor/redPandaProjects)
  // are intentionally not part of the patch.
  const buildInformationPatch = (overrides = {}) => {
    const patch = {};
    if (overrides.description !== undefined) {
      patch.description = overrides.description;
    }
    if (overrides.richDescription !== undefined) {
      patch.richDescription = overrides.richDescription;
    }
    if (overrides.project !== undefined) {
      patch.project = overrides.project;
    }
    if (overrides.reservedForUsers !== undefined) {
      patch.reservedForUsers = overrides.reservedForUsers;
    }
    return patch;
  };

  const runInformationUpsert = async (overrides) => {
    if (!informationSlotId) {
      showSnackbarMessage({ type: "error", message: "Information slot id missing" });
      return false;
    }
    const patch = buildInformationPatch(overrides);
    if (!Object.keys(patch).length) return true;
    if (startLoading) startLoading();
    try {
      if (informationBatchId) {
        // Cascade edit: resolve all sibling ids first, then apply the patch
        // to every slot in parallel via the single-slot CREATE upsert path.
        // BE has no atomic batchUpdate; partial failures are reported.
        const idsResult = await client.query({
          query: INFORMATION_BOOKING_IDS_BY_BATCH_ID,
          fetchPolicy: "no-cache",
          variables: { batchId: informationBatchId },
        });
        const ids = idsResult?.data?.informationBookingIdsByBatchId || [];
        if (ids.length === 0) {
          showSnackbarMessage({ type: "error", message: "Batch is no longer available" });
          return false;
        }
        // BE `updateInformationBooking` accepts a pure patch input -- no
        // immutable fields to re-send. Cascade is a simple fan-out over
        // sibling ids; partial failures are reported via Promise.allSettled.
        const results = await Promise.allSettled(
          ids.map((id) =>
            client.mutate({
              mutation: UPDATE_INFORMATION_BOOKING,
              fetchPolicy: "no-cache",
              variables: { input: { id, ...patch } },
            }),
          ),
        );
        const succeeded = results.filter((r) => r.status === "fulfilled").length;
        const failed = results.length - succeeded;
        if (succeeded === 0) {
          showSnackbarMessage({ type: "error", message: "Failed to update Information batch" });
          return false;
        }
        if (failed > 0) {
          showSnackbarMessage({
            type: "warning",
            message: `Updated ${succeeded} of ${results.length} Information slots (${failed} failed)`,
          });
        }
      } else {
        await client.mutate({
          mutation: UPDATE_INFORMATION_BOOKING,
          fetchPolicy: "no-cache",
          variables: { input: { id: informationSlotId, ...patch } },
        });
      }
      if (onUpdateComplete) onUpdateComplete();
      return true;
    } catch (error) {
      showSnackbarMessage({
        type: "error",
        message: error?.message || "Update failed",
      });
      return false;
    } finally {
      if (stopLoading) stopLoading();
    }
  };

  const saveInformationRichDescription = async ({ richDescription, description }) => {
    const ok = await runInformationUpsert({ richDescription, description });
    if (ok) {
      setRichDescriptionValue(richDescription);
      setDescriptionValue(description);
    } else {
      throw new Error("Failed to save description");
    }
  };

  const isInformationEditMode = !!informationSlotId;
  const informationUserFixed = useMemo(() => {
    if (!user) return null;
    return {
      key: user.email,
      familyName: user.familyName,
      givenName: user.givenName,
      name: user.name,
      userId: user.user,
      value: formatUserDisplay(user),
      email: user.email,
    };
  }, [user]);

  const informationUserOptions = useMemo(() => {
    const mapped = (usersList || []).map((item) => ({
      ...item,
      key: item.email,
      value: formatUserDisplay(item),
    }));
    const bookingCreatedBy = eventData?.createdBy ?? "";
    let userDetailArr;
    if (isInformationEditMode) {
      userDetailArr = mapped.filter((k) => k.userId === bookingCreatedBy);
      if (userDetailArr.length === 0 && informationUserFixed) {
        userDetailArr = [informationUserFixed];
      }
    } else {
      userDetailArr = informationUserFixed ? [informationUserFixed] : [];
    }
    const filtered = mapped.filter((a) =>
      isInformationEditMode ? a.userId !== bookingCreatedBy : a.key !== user?.email,
    );
    const sorted = [...filtered].sort((a, b) => String(a?.value || "").localeCompare(String(b?.value || "")));
    return uniqList([...userDetailArr, ...sorted], "value");
  }, [usersList, isInformationEditMode, eventData?.createdBy, user, informationUserFixed]);

  const informationSelectedUsers = useMemo(() => {
    const ids =
      Array.isArray(eventData?.reservedForUsers) && eventData.reservedForUsers.length > 0
        ? eventData.reservedForUsers
        : eventData?.reservedForEmails || [];
    return ids
      .map((uid) => (informationUserOptions || []).find((u) => u.userId === uid || u.email === uid))
      .filter(Boolean);
  }, [eventData?.reservedForUsers, eventData?.reservedForEmails, informationUserOptions]);

  const handleSaveInformationUsers = async (selectedUsers) => {
    const reservedForUsers = (selectedUsers || []).map((u) => u?.userId).filter(Boolean);
    if (reservedForUsers.length === 0) {
      showSnackbarMessage({ type: "error", message: "At least one user is required" });
      return false;
    }
    return runInformationUpsert({ reservedForUsers });
  };

  const isInformationPredUser = !!storeUserForProjects?.isPredUser;

  const getInformationProjectLabel = (project = {}) => {
    const themeNumber = project.themeNumber;
    const shortName = project.projectShortName || project.projectName || project.projectId;
    if (themeNumber && shortName) return `${themeNumber} - ${shortName}`;
    return shortName || themeNumber || "-";
  };

  const compareInformationProjects = (a = {}, b = {}) => {
    const themeA = String(a.themeNumber || "");
    const themeB = String(b.themeNumber || "");
    const themeCompare = themeA.localeCompare(themeB, undefined, { numeric: true, sensitivity: "base" });
    if (themeCompare !== 0) return themeCompare;
    const shortA = String(a.projectShortName || a.projectName || a.projectId || "");
    const shortB = String(b.projectShortName || b.projectName || b.projectId || "");
    return shortA.localeCompare(shortB, undefined, { numeric: true, sensitivity: "base" });
  };

  const informationProjectOptions = useMemo(() => {
    const userRp = Array.isArray(storeUserForProjects?.redPandaProjects)
      ? [...storeUserForProjects.redPandaProjects]
      : [];
    const sorted = userRp.sort(compareInformationProjects);
    const mapped = sorted.map((project) => ({
      value: {
        themeNumber: project.themeNumber,
        projectId: project.projectId,
        projectShortName: project.projectShortName,
        projectName: project.projectName,
      },
      name: getInformationProjectLabel(project),
    }));
    const fromBooking = Array.isArray(eventData?.redPandaProjects)
      ? eventData.redPandaProjects.map(({ __typename, ...rest }) => rest)
      : [];
    if (!fromBooking.length) return mapped;
    const existingIds = new Set(mapped.map((o) => o.value?.projectId).filter(Boolean));
    const additions = fromBooking
      .filter((p) => p?.projectId && !existingIds.has(p.projectId))
      .map((p) => ({ value: p, name: getInformationProjectLabel(p) }));
    if (!additions.length) return mapped;
    return [...mapped, ...additions].sort((a, b) => compareInformationProjects(a?.value, b?.value));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [storeUserForProjects?.redPandaProjects, eventData?.redPandaProjects]);

  const informationSelectedProjectOptions = useMemo(() => {
    const list = Array.isArray(eventData?.redPandaProjects) ? eventData.redPandaProjects : [];
    return list.map(({ __typename, ...rest }) => ({
      value: rest,
      name: getInformationProjectLabel(rest),
    }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [eventData?.redPandaProjects]);

  const handleSaveInformationProjects = async ({ redPandaProjects: rpInput, project: manualInput }) => {
    const redPandaProjects = (rpInput || []).map((wrapped) => {
      const raw = wrapped?.value || wrapped;
      return {
        projectId: raw.projectId,
        projectName: raw.projectName,
        projectShortName: raw.projectShortName,
        themeNumber: raw.themeNumber,
      };
    });
    const project = redPandaProjects.length > 0 ? null : manualInput && manualInput.trim() ? manualInput.trim() : null;
    return runInformationUpsert({ redPandaProjects, project });
  };

  const handleRichDescriptionSave = async ({ richDescription, description }) => {
    if (isInformationBooking) {
      return saveInformationRichDescription({ richDescription, description });
    }
    if (!bookingId) return;
    const previousRich = richDescriptionValue;
    const previousShort = descriptionValue;
    if (startLoading) startLoading();
    try {
      const result = await client.mutate({
        mutation: QUICK_EDIT_BOOKING,
        variables: {
          input: {
            bookingId,
            description,
            richDescription,
            createdByEmail: user?.email,
          },
        },
      });
      const payload = result?.data?.quickEditBooking;
      if (!payload?.success) {
        handleQuickEditFailure({
          stopLoading,
          setValue: setRichDescriptionValue,
          previousValue: previousRich,
          errorMessage: payload?.message || "Description update failed",
        });
        setDescriptionValue(previousShort);
        throw new Error(payload?.message || "Description update failed");
      }
      setRichDescriptionValue(richDescription);
      setDescriptionValue(description);
      if (payload?.commandId) {
        registerQuickEditTracking({
          payload,
          stopLoading,
          onUpdateComplete,
          setValue: setRichDescriptionValue,
          previousValue: previousRich,
        });
      } else {
        finishQuickEditWithoutTracking({ stopLoading, onUpdateComplete });
      }
    } catch (error) {
      handleQuickEditFailure({
        stopLoading,
        setValue: setRichDescriptionValue,
        previousValue: previousRich,
        errorMessage: error?.message || "Description update failed",
      });
      setDescriptionValue(previousShort);
      throw error;
    }
  };

  const handleHazardousSave = (nextValue) =>
    saveInlineTextField({
      nextValue,
      currentValue: hazardousValue,
      setValue: setHazardousValue,
      buildInput: (trimmed, resolvedBookingId) => ({
        bookingId: resolvedBookingId,
        hazardousDeclaration: {
          hasHazardousMaterials: !!eventData?.hazardousDeclaration?.hasHazardousMaterials,
          hazardousMaterialsList: trimmed,
        },
        createdByEmail: user?.email,
      }),
      errorMessage: "Hazardous materials update failed",
    });

  return (
    <>
      <div style={{ display: "flex", alignItems: "center", gap: "8px", margin: "1.125rem 0" }}>
        <OwcTypography variant="button">{title}</OwcTypography>
        <CustomChip
          text={`${slotsCount}`}
          color="#fff"
          bgColor="rgba(11, 65, 205, 1)"
          compact
          info="Timeslot position within booking"
        />
      </div>

      <OwcGrid
        container
        columns={{ xs: 1, s: 2, m: 2, l: 2, xl: 2 }}
        vGutter={{ m: 8, l: 8, xl: 8, xs: 8, s: 8 }}
        hGutter={0}
      >
        <OwcGrid
          item
          l={1}
          m={1}
          xs={2}
          s={2}
          style={{
            alignSelf: "start",
            paddingBottom: "0.25rem",
          }}
        >
          <DateTimeRange
            start={eventData?.start}
            end={eventData?.end}
            timezone={user?.timezone}
            repetitionPattern={eventData?.repetitionPattern}
            handleOpenChangeEndTimeModal={
              canChangeEndTime && !isInformationBooking ? handleOpenChangeEndTimeModal : undefined
            }
          />
        </OwcGrid>

        <ApprovalStatusSection isRunEvent={isRunEvent} approvalStatusProps={approvalStatusProps} />

        <OwcGrid
          item
          xl={1}
          l={1}
          m={1}
          xs={1}
          s={2}
          style={{
            alignSelf: "start",
            paddingBottom: "0.25rem",
          }}
        >
          <InlineRichDescriptionField
            label="Description"
            value={richDescriptionValue}
            allowEdit={canEditInline}
            required={isInformationBooking}
            placeholder="Enter description"
            onEditStart={handleInlineEditStart}
            onSave={handleRichDescriptionSave}
          />
        </OwcGrid>
        <HazardousMaterialsSection
          isRunEvent={isRunEvent}
          canEditInline={canEditInline}
          eventData={eventData}
          hazardousValue={hazardousValue}
          handleInlineEditStart={handleInlineEditStart}
          handleHazardousSave={handleHazardousSave}
        />

        <OwcGrid
          xl={1}
          item
          l={1}
          m={1}
          xs={1}
          s={2}
          style={{
            alignSelf: "start",
            paddingBottom: "0.25rem",
          }}
        >
          {isInformationBooking ? (
            <InformationProjectInlineEdit
              options={informationProjectOptions}
              selectedRedPandaProjectOptions={informationSelectedProjectOptions}
              legacyProject={typeof eventData?.project === "string" ? eventData.project : ""}
              isPredUser={isInformationPredUser}
              allowEdit={isBookingAdmin}
              onSave={handleSaveInformationProjects}
            />
          ) : (
            <ProjectInfo
              projectId={eventData?.project}
              redPandaProjects={eventData?.redPandaProjects}
              client={client}
              user={user}
            />
          )}
        </OwcGrid>
        <AssistanceDetailsSection isRunEvent={isRunEvent} assistanceDetailsProps={assistanceDetailsProps} />

        <ElnDetailsSection isRunEvent={isRunEvent} elnId={eventData?.elnId} />

        {!isInformationBooking && (
          <OwcGrid
            item
            xl={1}
            l={1}
            m={1}
            xs={1}
            s={1}
            style={{
              alignSelf: "start",
              paddingBottom: "0.25rem",
            }}
          >
            <ActionType type={eventData?.type} />
          </OwcGrid>
        )}

        <OwcGrid
          item
          xl={isInformationBooking ? 1 : 2}
          l={isInformationBooking ? 1 : 2}
          m={isInformationBooking ? 1 : 2}
          s={isInformationBooking ? 2 : 2}
          xs={isInformationBooking ? 1 : 2}
          style={{
            alignSelf: "start",
            paddingBottom: "0.25rem",
          }}
        >
          {isInformationBooking ? (
            <InformationFromInlineEdit
              options={informationUserOptions}
              selected={informationSelectedUsers}
              allowEdit={isBookingAdmin}
              onSave={handleSaveInformationUsers}
            />
          ) : (
            <BookedForUsers bookedForEmails={eventData?.reservedForEmails} usersList={usersList} />
          )}
        </OwcGrid>
        {!isInformationBooking && selectIsPredOrGredUser(user) && (
          <OwcGrid
            item
            xl={2}
            l={2}
            m={2}
            s={2}
            xs={2}
            style={{
              alignSelf: "start",
              paddingBottom: "0.25rem",
            }}
          >
            <CreatedByCreatedOn
              createdBy={eventData?.createdBy}
              createdByEmail={eventData?.createdByEmail}
              createdAt={eventData?.createdAt}
              usersList={usersList}
              timezone={user?.timezone}
            />
          </OwcGrid>
        )}
      </OwcGrid>
      <ChangeBookingEndTimeModal
        isOpen={isChangeEndTimeOpen}
        onClose={handleCloseChangeEndTimeModal}
        booking={bookingForEndTimeChange || eventData}
        onSave={handleSaveEndTime}
        initialSelectedResourceId={eventData?.resource}
      />
    </>
  );
};

export default BookingEventDetails;
